import * as React from 'react';
import { FC } from 'react';

import { capitalize } from 'lodash';
import { useIntl } from 'react-intl';

import { IOfferDiscount } from '@rbi-ctg/menu';
import LoyaltyPointsIcon from 'components/icons/loyalty-points-icon';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { usePrevious } from 'hooks/use-previous';
import { selectors, useAppSelector } from 'state/global-state';
import { getRewardPrice } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { isDiscountReward } from 'state/loyalty/types';
import { calculateDiscountValue, getItemLoyaltyRewardDiscount } from 'utils/cart/helper';
import { delayUnmountHOC } from 'utils/delay-unmount-hoc';
import priceForCartEntry from 'utils/menu/price-for-cart-entry';

import { IPrice } from '../types';

import {
  CartItemPrice,
  CurrencyContainer,
  FreeMessageContainer,
  StrikeThrough,
  StyledCurrency,
  StyledDiscountPrice,
} from './styled';

const DelayedUnmountPrice = delayUnmountHOC(StyledDiscountPrice);

const PriceFree = () => {
  const { formatMessage } = useIntl();

  return (
    <FreeMessageContainer>
      <LoyaltyPointsIcon />
      {capitalize(formatMessage({ id: 'free' }))}
    </FreeMessageContainer>
  );
};

const RewardDiscountPrice = ({
  rewardApplied,
  showPriceInsteadFreeInCart,
  discountPrice,
}: {
  rewardApplied: boolean;
  showPriceInsteadFreeInCart: boolean;
  discountPrice: number;
}) => {
  const prevDiscountPrice = usePrevious(discountPrice);
  const discountDisplay = rewardApplied ? discountPrice : prevDiscountPrice;

  if (showPriceInsteadFreeInCart) {
    return null;
  }

  if (rewardApplied && discountPrice === 0) {
    return <PriceFree />;
  }

  return (
    <div>
      <DelayedUnmountPrice delayTime={300} isVisible={rewardApplied} amount={discountDisplay} />
    </div>
  );
};

const Price: FC<IPrice> = ({ item, isOffer, className, rewardApplied }) => {
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const showPriceInsteadFreeInCart = useFlag(LaunchDarklyFlag.SHOW_PRICE_INSTEAD_FREE_IN_CART);
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const { sanityRewardsMap } = useLoyaltyRewardsList();

  const isRewardApplied = Boolean(rewardApplied && !isOffer);
  const showStrike = isRewardApplied && !showPriceInsteadFreeInCart;

  const appliedReward = appliedLoyaltyRewards[item.cartId || 'discount-offer'];
  const sanityReward = appliedReward?.sanityId ? sanityRewardsMap?.[appliedReward.sanityId] : null;
  const rewardPrice =
    sanityRewardsMap && appliedReward?.sanityId
      ? getRewardPrice(sanityRewardsMap?.[appliedReward.sanityId])
      : false;
  const price = priceForCartEntry(item);

  let discountPrice =
    price - getItemLoyaltyRewardDiscount({ cartEntry: item, appliedLoyaltyRewards });

  // For discounted products we are calculating the discount only for the base item price
  // not including other options added by user when customizing reward item
  if (isDiscountReward(sanityReward)) {
    discountPrice =
      price - calculateDiscountValue(item.price!, sanityReward?.incentives?.[0] as IOfferDiscount);
  }

  // If rewardPrice rule is set, the discountPrice is the final price of the reward
  if (rewardPrice) {
    discountPrice = rewardPrice;
  }

  if (isNaN(discountPrice)) {
    discountPrice = 0;
  }

  return (
    <CartItemPrice className={className} isOffer={isOffer}>
      <CurrencyContainer isRewardApplied={isRewardApplied}>
        {showStrike && <StrikeThrough />}
        <StyledCurrency amount={showPriceInsteadFreeInCart ? discountPrice : price} />
      </CurrencyContainer>
      {loyaltyEnabled && (
        <RewardDiscountPrice
          discountPrice={discountPrice}
          rewardApplied={isRewardApplied}
          showPriceInsteadFreeInCart={showPriceInsteadFreeInCart}
        />
      )}
    </CartItemPrice>
  );
};

export default Price;
